import { 
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { sendNotification } from './notificationService';
import { getAllOrders } from './orderService';
import { sendReturnRequestToAdmin, sendReturnApprovedToCustomer } from './emailService';
import { refundPayment as refundQNBPayment } from './qnbPaymentService';

// ⚙️ AYARLAR
const ADMIN_EMAIL = 'info@tronapazarlama.com';

// ✅ ŞİRKET İADE ADRESİ
const COMPANY_RETURN_ADDRESS = {
  companyName: 'Trona Pazarlama',
  address: 'Manavgat Mahallesi, Örnek Sokak No:123',
  district: 'Manavgat',
  city: 'Antalya',
  zipCode: '07600',
  phone: '05551234567',
  email: 'iade@tronapazarlama.com'
};

// ==================== İADE TALEBİ OLUŞTUR ====================
export const requestOrderReturn = async (orderId, returnData) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    
    // ✅ Sadece teslim edilmiş siparişler iade edilebilir
    if (currentOrder.status !== 'delivered') {
      return { 
        success: false, 
        error: 'İade sadece teslim edilmiş siparişler için yapılabilir.' 
      };
    }
    
    // ✅ Teslim tarihinden 7 gün kontrolü
    if (currentOrder.deliveredAt) {
      try {
        let deliveryDate;
        
        if (currentOrder.deliveredAt.toDate && typeof currentOrder.deliveredAt.toDate === 'function') {
          deliveryDate = currentOrder.deliveredAt.toDate();
        } else if (currentOrder.deliveredAt instanceof Date) {
          deliveryDate = currentOrder.deliveredAt;
        } else {
          deliveryDate = new Date(currentOrder.deliveredAt);
        }
        
        const now = new Date();
        const daysPassed = Math.floor((now - deliveryDate) / (1000 * 60 * 60 * 24));
        
        if (daysPassed > 7) {
          return { 
            success: false, 
            error: 'İade süresi dolmuştur. Teslim tarihinden itibaren 7 gün içinde iade yapılabilir.' 
          };
        }
      } catch (dateError) {
        console.warn('Tarih parse hatası, devam ediliyor:', dateError);
      }
    }
    
    const returnRequest = {
      requestId: `RETURN-${Date.now()}`,
      orderId: orderId,
      orderNumber: currentOrder.orderNumber,
      reason: returnData.reason,
      reasonDetail: returnData.reasonDetail || '',
      returnType: returnData.returnType || 'full',
      itemsToReturn: returnData.itemsToReturn || currentOrder.items,
      hasDefect: returnData.hasDefect || false,
      defectDescription: returnData.defectDescription || '',
      images: returnData.images || [],
      requestedBy: returnData.userId || 'customer',
      requestedAt: new Date().toISOString(),
      status: 'pending',
      refundAmount: returnData.refundAmount || currentOrder.total,
      refundMethod: currentOrder.paymentMethod === 'credit-card' ? 'card' : 'cash',
      
      // ✅ Kargo bilgileri (henüz yok)
      returnTrackingNumber: null,
      returnCarrier: null,
      returnShippedAt: null,
      returnReceivedAt: null
    };
    
    await updateDoc(orderRef, {
      returnRequest: returnRequest,
      returnStatus: 'pending',
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_requested',
      action: 'İade talebi oluşturuldu',
      reason: returnData.reason,
      date: new Date().toISOString(),
      updatedBy: 'Müşteri'
    });
    
    await updateDoc(orderRef, {
      timeline: timeline
    });
    
    await sendNotification(
      'return',
      'İade Talebi Oluşturuldu',
      `${currentOrder.orderNumber} numaralı sipariş için iade talebi oluşturuldu.`,
      currentOrder.userId
    );
    
    // ✅ ADMİN'E İADE TALEBİ BİLDİRİMİ
    try {
      await sendReturnRequestToAdmin({
        orderNumber: currentOrder.orderNumber,
        customerName: `${currentOrder.shippingAddress.firstName} ${currentOrder.shippingAddress.lastName}`,
        reason: returnData.reason,
        reasonDetail: returnData.reasonDetail || '',
        total: returnData.refundAmount || currentOrder.total,
        items: returnData.itemsToReturn || currentOrder.items
      }, ADMIN_EMAIL);
      console.log('✅ Admin\'e iade talebi bildirimi gönderildi');
    } catch (mailError) {
      console.error('❌ İade talebi maili gönderilemedi:', mailError);
    }
    
    return { 
      success: true, 
      message: 'İade talebiniz alındı. Talebiniz değerlendirildikten sonra kargo bilgileri size iletilecektir.',
      requestId: returnRequest.requestId
    };
  } catch (error) {
    console.error('İade talebi oluşturulamadı:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ADMİN: İADE TALEBİNİ ONAYLA + SÜRAT KARGO ETİKETİ OLUŞTUR ====================
export const approveReturn = async (orderId, approvalData) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    const returnRequest = currentOrder.returnRequest;
    
    if (!returnRequest || returnRequest.status !== 'pending') {
      return { success: false, error: 'Bekleyen iade bulunamadı' };
    }
    
    console.log('📦 İade onaylanıyor ve Sürat Kargo etiketi oluşturuluyor...');
    
    // ✅ SÜRAT KARGO İADE ETİKETİ OLUŞTUR
    let returnTrackingNumber = null;
    let returnCarrier = 'Sürat Kargo';
    let cargoError = null;
    
    try {
      const createReturnShipmentFn = httpsCallable(functions, 'createReturnShipment');
      
      const cargoResult = await createReturnShipmentFn({
        orderData: {
          orderNumber: currentOrder.orderNumber,
          customerName: `${currentOrder.shippingAddress.firstName} ${currentOrder.shippingAddress.lastName}`,
          shippingAddress: currentOrder.shippingAddress,
          total: currentOrder.total
        },
        returnAddress: COMPANY_RETURN_ADDRESS
      });
      
      if (cargoResult.data.success) {
        returnTrackingNumber = cargoResult.data.returnTrackingNumber;
        console.log('✅ İade kargo etiketi oluşturuldu:', returnTrackingNumber);
      } else {
        cargoError = cargoResult.data.error || 'Kargo etiketi oluşturulamadı';
        console.error('❌ Kargo hatası:', cargoError);
      }
    } catch (cargoErr) {
      cargoError = cargoErr.message;
      console.error('❌ Kargo exception:', cargoErr);
    }
    
    // ✅ İade talebini güncelle
    returnRequest.status = 'approved';
    returnRequest.approvedBy = approvalData.adminId || 'admin';
    returnRequest.approvedAt = new Date().toISOString();
    returnRequest.approvalNote = approvalData.note || '';
    returnRequest.returnTrackingNumber = returnTrackingNumber;
    returnRequest.returnCarrier = returnCarrier;
    returnRequest.returnAddress = COMPANY_RETURN_ADDRESS;
    returnRequest.cargoError = cargoError;
    
    await updateDoc(orderRef, {
      returnStatus: 'approved',
      returnRequest: returnRequest,
      returnTrackingNumber: returnTrackingNumber, // ✅ Üst seviyeye de yaz
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_approved',
      action: returnTrackingNumber 
        ? `İade onaylandı - Takip No: ${returnTrackingNumber}`
        : 'İade onaylandı - Kargo etiketi manuel oluşturulmalı',
      returnTrackingNumber: returnTrackingNumber,
      date: new Date().toISOString(),
      updatedBy: 'Admin'
    });
    
    await updateDoc(orderRef, { timeline });
    
    // ✅ MÜŞTERİYE MAİL GÖNDER
    try {
      await sendReturnApprovedToCustomer({
        orderNumber: currentOrder.orderNumber,
        customerName: `${currentOrder.shippingAddress.firstName} ${currentOrder.shippingAddress.lastName}`,
        customerEmail: currentOrder.customerEmail,
        returnTrackingNumber: returnTrackingNumber,
        returnAddress: COMPANY_RETURN_ADDRESS,
        returnInstructions: `
1. Ürünü orijinal ambalajında paketleyin
2. İade etiketini pakete yapıştırın
3. En yakın Sürat Kargo şubesine götürün
4. Kargo takip numarası: ${returnTrackingNumber || 'Manuel etiket alınız'}
5. İade adres: ${COMPANY_RETURN_ADDRESS.address}, ${COMPANY_RETURN_ADDRESS.district}/${COMPANY_RETURN_ADDRESS.city}
        `
      });
      console.log('✅ Müşteriye iade onay maili gönderildi');
    } catch (mailError) {
      console.error('❌ Mail gönderilemedi:', mailError);
    }
    
    await sendNotification(
      'return',
      'İadeniz Onaylandı!',
      returnTrackingNumber
        ? `${currentOrder.orderNumber} numaralı siparişinizin iadesi onaylandı. Takip No: ${returnTrackingNumber}`
        : `${currentOrder.orderNumber} numaralı siparişinizin iadesi onaylandı. Kargo bilgileri e-postanıza gönderildi.`,
      currentOrder.userId
    );
    
    return { 
      success: true, 
      message: returnTrackingNumber
        ? `İade onaylandı. Takip No: ${returnTrackingNumber}`
        : 'İade onaylandı ama kargo etiketi manuel oluşturulmalı.',
      returnTrackingNumber: returnTrackingNumber,
      requiresManualCargo: !returnTrackingNumber
    };
  } catch (error) {
    console.error('İade onaylanamadı:', error);
    return { success: false, error: error.message };
  }
};

// ==================== MÜŞTERİ: "KARGOYA VERDİM" ====================
export const markReturnShipped = async (orderId) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    const returnRequest = currentOrder.returnRequest;
    
    if (!returnRequest || returnRequest.status !== 'approved') {
      return { success: false, error: 'Onaylanmış iade bulunamadı' };
    }
    
    returnRequest.status = 'in_transit';
    returnRequest.returnShippedAt = new Date().toISOString();
    
    await updateDoc(orderRef, {
      returnStatus: 'in_transit',
      returnRequest: returnRequest,
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_in_transit',
      action: 'Müşteri kargoya verdi',
      returnTrackingNumber: returnRequest.returnTrackingNumber,
      date: new Date().toISOString(),
      updatedBy: 'Müşteri'
    });
    
    await updateDoc(orderRef, { timeline });
    
    await sendNotification(
      'return',
      'İade Kargonuz Yolda',
      `${currentOrder.orderNumber} numaralı siparişinizin iadesi kargoya verildi.`,
      currentOrder.userId
    );
    
    return { 
      success: true, 
      message: 'İade kargonuz kaydedildi.'
    };
  } catch (error) {
    console.error('İade kargo kaydedilemedi:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ADMİN: "TESLİM ALDIK" ====================
export const markReturnReceived = async (orderId, receiptData) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    const returnRequest = currentOrder.returnRequest;
    
    if (!returnRequest || returnRequest.status !== 'in_transit') {
      return { success: false, error: 'Kargoda iade bulunamadı' };
    }
    
    returnRequest.status = 'received';
    returnRequest.receivedBy = receiptData.adminId || 'admin';
    returnRequest.returnReceivedAt = new Date().toISOString();
    returnRequest.productCondition = receiptData.productCondition || 'good';
    returnRequest.inspectionNote = receiptData.inspectionNote || '';
    
    await updateDoc(orderRef, {
      returnStatus: 'received',
      returnRequest: returnRequest,
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_received',
      action: 'İade ürünü depoya ulaştı - İnceleniyor',
      productCondition: receiptData.productCondition,
      date: new Date().toISOString(),
      updatedBy: 'Admin'
    });
    
    await updateDoc(orderRef, { timeline });
    
    await sendNotification(
      'return',
      'İade Ürününüz Teslim Alındı',
      `${currentOrder.orderNumber} numaralı siparişinizin iadesi depoya ulaştı ve inceleniyor.`,
      currentOrder.userId
    );
    
    return { 
      success: true, 
      message: 'İade ürünü teslim alındı olarak işaretlendi.'
    };
  } catch (error) {
    console.error('İade teslim alınamadı:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ADMİN: İADEYİ TAMAMLA + QNB REFUND ====================
export const completeReturn = async (orderId, completionData) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    const returnRequest = currentOrder.returnRequest;
    
    if (!returnRequest || returnRequest.status !== 'received') {
      return { success: false, error: 'Teslim alınmış iade bulunamadı' };
    }
    
    // ✅ QNB İADE İŞLEMİ
    let qnbRefundSuccess = false;
    let refundError = null;
    
    if (currentOrder.paymentStatus === 'paid' && currentOrder.paymentMethod === 'credit-card') {
      try {
        const qnbOrderId = currentOrder.orderId || currentOrder.orderNumber;
        const refundAmount = returnRequest.refundAmount || currentOrder.total;
        
        console.log('💰 QNB İade başlatılıyor:', {
          orderId: qnbOrderId,
          amount: refundAmount
        });
        
        const refundResult = await refundQNBPayment(qnbOrderId, refundAmount);
        
        if (refundResult.success) {
          console.log('✅ QNB iade başarılı');
          qnbRefundSuccess = true;
        } else {
          console.warn('⚠️ QNB iade başarısız:', refundResult.error);
          refundError = refundResult.error;
        }
      } catch (qnbError) {
        console.error('❌ QNB iade hatası:', qnbError);
        refundError = qnbError.message;
      }
    } else {
      // Nakit ödeme veya ödeme yapılmamış
      qnbRefundSuccess = true; // Manuel iade olacak
    }
    
    // ✅ STOK GERİ YÜKLE (Ürün iyi durumdaysa)
    if (returnRequest.productCondition === 'good') {
      const batch = writeBatch(db);
      for (const item of returnRequest.itemsToReturn || currentOrder.items) {
        const productRef = doc(db, 'Products', item.productId);
        batch.update(productRef, {
          stock: increment(item.quantity)
        });
      }
      await batch.commit();
      console.log('✅ Stok geri yüklendi');
    }
    
    returnRequest.status = 'completed';
    returnRequest.completedBy = completionData.adminId || 'admin';
    returnRequest.completedAt = new Date().toISOString();
    returnRequest.refundProcessed = qnbRefundSuccess;
    returnRequest.refundTransactionId = completionData.refundTransactionId || `REF-${Date.now()}`;
    returnRequest.requiresManualRefund = !qnbRefundSuccess;
    returnRequest.refundError = refundError;
    
    await updateDoc(orderRef, {
      status: 'returned',
      returnStatus: 'completed',
      returnRequest: returnRequest,
      refundedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_completed',
      action: qnbRefundSuccess 
        ? 'İade tamamlandı - Ödeme iade edildi' 
        : 'İade tamamlandı - Manuel ödeme iadesi gerekli',
      refundProcessed: qnbRefundSuccess,
      date: new Date().toISOString(),
      updatedBy: 'Admin'
    });
    
    await updateDoc(orderRef, { timeline });
    
    await sendNotification(
      'return',
      'İadeniz Tamamlandı!',
      `${currentOrder.orderNumber} numaralı siparişinizin iade işlemi tamamlandı. ${
        qnbRefundSuccess 
          ? 'Ödemeniz hesabınıza yansıyacaktır.' 
          : 'Ödemeniz manuel olarak işleme alınacaktır.'
      }`,
      currentOrder.userId
    );
    
    return { 
      success: true, 
      message: qnbRefundSuccess
        ? 'İade işlemi tamamlandı ve ödeme iade edildi.'
        : 'İade işlemi tamamlandı. Ödeme manuel olarak iade edilmelidir.',
      requiresManualRefund: !qnbRefundSuccess,
      refundError: refundError
    };
  } catch (error) {
    console.error('İade tamamlanamadı:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ADMİN: İADEYİ REDDET ====================
export const rejectReturn = async (orderId, rejectionData) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }
    
    const currentOrder = orderDoc.data();
    const returnRequest = currentOrder.returnRequest;
    
    if (!returnRequest) {
      return { success: false, error: 'İade talebi bulunamadı' };
    }
    
    returnRequest.status = 'rejected';
    returnRequest.rejectedBy = rejectionData.adminId || 'admin';
    returnRequest.rejectedAt = new Date().toISOString();
    returnRequest.rejectionReason = rejectionData.reason;
    returnRequest.rejectionDetail = rejectionData.detail || '';
    
    await updateDoc(orderRef, {
      returnStatus: 'rejected',
      returnRequest: returnRequest,
      updatedAt: serverTimestamp()
    });
    
    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: 'return_rejected',
      action: 'İade talebi reddedildi',
      reason: rejectionData.reason,
      date: new Date().toISOString(),
      updatedBy: 'Admin'
    });
    
    await updateDoc(orderRef, { timeline });
    
    await sendNotification(
      'return',
      'İade Talebiniz Reddedildi',
      `${currentOrder.orderNumber} numaralı siparişinizin iade talebi reddedildi. Neden: ${rejectionData.reason}`,
      currentOrder.userId
    );
    
    return { 
      success: true, 
      message: 'İade talebi reddedildi.'
    };
  } catch (error) {
    console.error('İade reddedilemedi:', error);
    return { success: false, error: error.message };
  }
};

// ==================== BEKLEYEN İADELERİ GETİR ====================
export const getPendingReturns = async () => {
  try {
    const allOrders = await getAllOrders();
    
    if (!allOrders.success) {
      return { success: false, error: 'Siparişler alınamadı' };
    }
    
    const pendingReturns = allOrders.orders.filter(order => 
      order.returnStatus && ['pending', 'approved', 'in_transit', 'received'].includes(order.returnStatus)
    );
    
    return {
      success: true,
      returns: pendingReturns,
      totalPending: pendingReturns.length
    };
  } catch (error) {
    console.error('Bekleyen iade talepleri alınamadı:', error);
    return { success: false, error: error.message };
  }
};

export default {
  requestOrderReturn,
  approveReturn,
  markReturnShipped,
  markReturnReceived,
  completeReturn,
  rejectReturn,
  getPendingReturns
};